import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TicketImageInterface } from "../../../../models/TicketModule/TicketImage";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export function ImageGalleryModal({
  open,
  images,
  startIndex,
  onClose,
}: {
  open: boolean;
  images: TicketImageInterface[];
  startIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startIndex);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  // swipe / drag tracking
  const startX = useRef<number | null>(null);
  const deltaX = useRef<number>(0);
  const dragging = useRef(false);

  useEffect(() => {
    if (!open) return;
    setIndex(startIndex);
  }, [open, startIndex]);

  const count = images?.length ?? 0;
  const safeIndex = useMemo(() => {
    if (!count) return 0;
    return Math.min(Math.max(index, 0), count - 1);
  }, [index, count]);

  const currentUrl = images?.[safeIndex]?.imageUrl;

  const goPrev = useCallback(() => {
    if (!count) return;
    setIndex((v) => (v - 1 + count) % count);
  }, [count]);

  const goNext = useCallback(() => {
    if (!count) return;
    setIndex((v) => (v + 1) % count);
  }, [count]);

  // keyboard support
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, goPrev, goNext, onClose]);

  // prevent background scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const onOverlayMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // close only when clicking the overlay (not the image/controls)
      if (e.target === overlayRef.current) onClose();
    },
    [onClose]
  );

  // pointer / touch swipe
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    deltaX.current = 0;
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current || startX.current === null) return;
    deltaX.current = e.clientX - startX.current;
  }, []);

  const onPointerUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;

    const dx = deltaX.current;
    startX.current = null;
    deltaX.current = 0;

    // swipe threshold
    const TH = 60;
    if (dx > TH) goPrev();
    else if (dx < -TH) goNext();
  }, [goPrev, goNext]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (!e.touches?.length) return;
    startX.current = e.touches[0].clientX;
    deltaX.current = 0;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (startX.current === null || !e.touches?.length) return;
    deltaX.current = e.touches[0].clientX - startX.current;
  }, []);

  const onTouchEnd = useCallback(() => {
    const dx = deltaX.current;
    startX.current = null;
    deltaX.current = 0;

    const TH = 60;
    if (dx > TH) goPrev();
    else if (dx < -TH) goNext();
  }, [goPrev, goNext]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-[2px] flex items-center justify-center"
      onMouseDown={onOverlayMouseDown}
      role="dialog"
      aria-modal="true"
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
        aria-label="Close"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium">
        {count ? `${safeIndex + 1} / ${count}` : "0 / 0"}
      </div>

      {/* Left / Right */}
      {count > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-3 md:left-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={goNext}
            className="absolute right-3 md:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Image */}
      <div
        className="max-w-[92vw] max-h-[88vh] w-full h-full flex items-center justify-center px-3"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={`Ticket image ${safeIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            draggable={false}
          />
        ) : (
          <div className="text-white/80 text-sm">No image</div>
        )}
      </div>
    </div>
  );
}
